import {
  mapLinkableEntityType,
  mapVenueDetailsType,
  useFindAllVenueOperatorsSuspense,
  useFindVenueSuspense,
} from '@api';
import {
  CompanyAnchor,
  DeleteActionIcon,
  EditActionIcon,
  TouringCard,
  TouringCardActions,
  TouringCardHeader,
  TouringCardSection,
  TouringCardTitle,
} from '@common';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { usePermissionContext } from '@hypeit-gmbh/hypeit-ui-core';
import { Button, Group } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { Route } from 'src/routes/_rootLayout/venues/$id.general';
import { EditVenueOperatorModal, VenueOperatorTable } from './components';
import { useDeleteVenueOperator, useUpdateVenueOperator } from './hooks';

export const VenueOperator: React.FC = () => {
  const { t } = useTranslation('venueDetailsGeneral');
  const { id } = Route.useParams();
  const [openedEditModal, { open: openEditModal, close: closeEditModal }] =
    useDisclosure(false);
  const { data: availableOperators } = useFindAllVenueOperatorsSuspense({
    query: {
      select: (data) => data.data.map(mapLinkableEntityType),
    },
  });
  const { data: venueOperator } = useFindVenueSuspense(id, {
    query: { select: (data) => mapVenueDetailsType(data.data).venueOperator },
  });

  const { hasPermission } = usePermissionContext();

  const { handleDelete } = useDeleteVenueOperator({ venueId: id });
  const { handleUpdate, isPending } = useUpdateVenueOperator({ venueId: id });

  const handleSave = async (operatorId: string) => {
    await handleUpdate(operatorId);
    closeEditModal();
  };

  const userCanEditVenueOperator = hasPermission({
    permission: 'VENUES_GENERAL',
    action: 'WRITE',
  });

  return (
    <>
      {venueOperator ? (
        <TouringCard data-testid="venue-operator-card">
          <TouringCardHeader>
            <Group gap="xs">
              <TouringCardTitle>{t('VENUE_OPERATOR.TITLE')}:</TouringCardTitle>
              <CompanyAnchor company={venueOperator.company} />
            </Group>
            {userCanEditVenueOperator && (
              <TouringCardActions>
                <EditActionIcon onClick={openEditModal} />
                <DeleteActionIcon onClick={handleDelete} />
              </TouringCardActions>
            )}
          </TouringCardHeader>
          <TouringCardSection>
            <VenueOperatorTable venueOperator={venueOperator} />
          </TouringCardSection>
        </TouringCard>
      ) : (
        <Button
          w="150px"
          color="blue"
          variant="outline"
          onClick={openEditModal}
          leftSection={<FontAwesomeIcon icon={faPlus} />}
        >
          {t('VENUE_OPERATOR.ADD_OPERATOR')}
        </Button>
      )}
      <EditVenueOperatorModal
        opened={openedEditModal}
        onClose={closeEditModal}
        onSave={handleSave}
        availableOperators={availableOperators}
        currentOperatorId={venueOperator?.company.id ?? null}
        isPending={isPending}
      />
    </>
  );
};
